// src/components/CollectionTransferModal.jsx
//
// Modal pour l'art advisor : transférer une ou plusieurs collections vers un client.
// Appelle l'edge function initiate-collection-transfer.
//
import React, { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { toast } from "react-hot-toast";
import { Info } from "lucide-react";

export default function CollectionTransferModal({ ownCollections, onClose }) {
  const [step, setStep] = useState(1); // 1 = choix collections, 2 = destinataire, 3 = confirmation
  const [selectedIds, setSelectedIds] = useState([]);
  const [toEmail, setToEmail] = useState("");
  const [permissionAfter, setPermissionAfter] = useState("can_edit");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggleCollection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!toEmail || selectedIds.length === 0) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/initiate-collection-transfer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            to_email: toEmail,
            collection_ids: selectedIds,
            permission_after: permissionAfter,
            message: message || null,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Erreur");
      setResult(data);
      setStep(3);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedCollections = ownCollections.filter((c) => selectedIds.includes(c.id));

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000,
    }}>
      <div style={{
        background: "#fff", borderRadius: 8, padding: 32,
        width: "100%", maxWidth: 540, maxHeight: "88vh", overflowY: "auto",
      }}>

        {/* ── Étape 1 : Choix des collections ── */}
        {step === 1 && (
          <>
            <h2 style={sTitle}>Transférer vers un client</h2>
            <p style={sSub}>
              Sélectionnez les collections à transférer. Vous conserverez un accès en tant qu'advisor.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
              {ownCollections.map((col) => {
                const selected = selectedIds.includes(col.id);
                return (
                  <button
                    key={col.id}
                    onClick={() => toggleCollection(col.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "12px 16px", borderRadius: 6, cursor: "pointer",
                      border: selected ? "2px solid #1a1a1a" : "1px solid #e0d8cc",
                      background: selected ? "#fafaf8" : "#fff",
                      textAlign: "left",
                    }}
                  >
                    <div style={{
                      width: 18, height: 18, borderRadius: 3, flexShrink: 0,
                      border: selected ? "none" : "2px solid #ccc",
                      background: selected ? "#1a1a1a" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {selected && <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>✓</span>}
                    </div>
                    <div>
                      <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a" }}>
                        {col.title}
                      </div>
                      <div style={{ fontSize: 11, color: "#aaa", fontFamily: "system-ui" }}>
                        {col.artworkCount || 0} œuvre{(col.artworkCount || 0) !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={onClose} style={sBtnSecondary}>Annuler</button>
              <button
                onClick={() => setStep(2)}
                disabled={selectedIds.length === 0}
                style={{ ...sBtnPrimary, opacity: selectedIds.length === 0 ? 0.4 : 1 }}
              >
                Suivant →
              </button>
            </div>
          </>
        )}

        {/* ── Étape 2 : Destinataire et options ── */}
        {step === 2 && (
          <>
            <h2 style={sTitle}>Destinataire</h2>
            <p style={sSub}>
              {selectedIds.length} collection{selectedIds.length > 1 ? "s" : ""} sélectionnée{selectedIds.length > 1 ? "s" : ""} :
              <span style={{ color: "#1a1a1a", fontWeight: 600 }}>
                {" "}{selectedCollections.map((c) => c.title).join(", ")}
              </span>
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={sLabel}>Email du client destinataire *</label>
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="client@exemple.fr"
                style={sInput}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={sLabel}>Votre accès après transfert</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { value: "can_edit", label: "Modification", desc: "Vous pouvez modifier les œuvres" },
                  { value: "can_view", label: "Consultation", desc: "Lecture seule" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setPermissionAfter(opt.value)}
                    style={{
                      flex: 1, padding: "10px 12px", borderRadius: 6, cursor: "pointer",
                      border: permissionAfter === opt.value ? "2px solid #1a1a1a" : "1px solid #e0d8cc",
                      background: permissionAfter === opt.value ? "#fafaf8" : "#fff",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, fontFamily: "system-ui", color: "#1a1a1a" }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: 11, color: "#888", fontFamily: "system-ui", marginTop: 2 }}>
                      {opt.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={sLabel}>Message au client (optionnel)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                placeholder="Ex : Voici votre collection Fondation Champ Libre…"
                style={{ ...sInput, resize: "vertical" }}
              />
            </div>

            <div style={{
              padding: "12px 14px", background: "#fafaf8", border: "1px solid #e0d8cc",
              borderRadius: 6, marginBottom: 20, display: "flex", gap: 10,
            }}>
              <Info size={14} color="#c9a961" style={{ flexShrink: 0, marginTop: 1 }} />
              <p style={{ fontSize: 12, color: "#555", fontFamily: "system-ui", margin: 0 }}>
                Si le client n'a pas encore de compte Artkiv, le transfert sera appliqué automatiquement
                à son inscription avec cet email.
              </p>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setStep(1)} style={sBtnSecondary}>← Retour</button>
              <button
                onClick={handleSubmit}
                disabled={!toEmail || loading}
                style={{ ...sBtnPrimary, opacity: (!toEmail || loading) ? 0.4 : 1 }}
              >
                {loading ? "Envoi…" : "Envoyer l'invitation"}
              </button>
            </div>
          </>
        )}

        {/* ── Étape 3 : Confirmation ── */}
        {step === 3 && result && (
          <>
            <div style={{ textAlign: "center", padding: "16px 0 24px" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✉️</div>
              <h2 style={{ ...sTitle, textAlign: "center" }}>Invitation envoyée</h2>
              <p style={{ fontSize: 14, color: "#555", fontFamily: "system-ui", marginBottom: 8 }}>
                {result.recipient_has_account
                  ? `${toEmail} a un compte Artkiv. L'invitation lui a été envoyée.`
                  : `${toEmail} n'a pas encore de compte. Le transfert s'appliquera automatiquement à son inscription.`
                }
              </p>
              <p style={{ fontSize: 13, color: "#aaa", fontFamily: "Georgia, serif", fontStyle: "italic" }}>
                Collections : {result.collections?.join(", ")}
              </p>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button onClick={onClose} style={sBtnPrimary}>Fermer</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const sTitle = { fontFamily: "Georgia, serif", fontSize: 20, color: "#1a1a1a", margin: "0 0 8px" };
const sSub = { fontSize: 13, color: "#888", fontFamily: "system-ui", margin: "0 0 20px" };
const sLabel = { display: "block", fontSize: 11, color: "#888", fontFamily: "system-ui", marginBottom: 5 };
const sInput = {
  width: "100%", boxSizing: "border-box", border: "1px solid #e0d8cc",
  borderRadius: 4, padding: "8px 10px", fontSize: 13,
  fontFamily: "Georgia, serif", color: "#1a1a1a", outline: "none", background: "#fff",
};
const sBtnPrimary = {
  padding: "9px 20px", background: "#1a1a1a", color: "#fff",
  border: "none", borderRadius: 4, fontSize: 13,
  fontFamily: "system-ui", fontWeight: 600, cursor: "pointer",
};
const sBtnSecondary = {
  padding: "9px 20px", background: "#fff", color: "#555",
  border: "1px solid #e0d8cc", borderRadius: 4, fontSize: 13,
  fontFamily: "system-ui", fontWeight: 600, cursor: "pointer",
};
